import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useDebounce } from '../hooks';
import { useStoredState } from '../hooks';

/**
 * Endpoints behind the command palette's patron and item search. They are the standard list routes
 * (`GET <path>?q=&pageSize=`); change them here if a feature module publishes different ones.
 */
export const SEARCH_ENDPOINTS = { patrons: '/patrons', biblios: '/catalog/biblios' } as const;

export interface PatronHit {
  id: number;
  cardNumber: string;
  name: string;
  detail: string;
}
export interface BiblioHit {
  id: number;
  title: string;
  detail: string;
}

type Loose = Record<string, unknown>;
const text = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');

/** Accepts either the `{ data, meta }` envelope or a bare array, and ignores anything unexpected. */
export function rowsOf(payload: unknown): Loose[] {
  const list = Array.isArray(payload) ? payload : payload && typeof payload === 'object' && Array.isArray((payload as Loose).data) ? ((payload as Loose).data as unknown[]) : [];
  return list.filter((r): r is Loose => Boolean(r) && typeof r === 'object');
}

export function toPatronHits(payload: unknown): PatronHit[] {
  return rowsOf(payload)
    .filter((r) => typeof r.id === 'number')
    .map((r) => ({
      id: r.id as number,
      cardNumber: text(r.cardNumber),
      name: [text(r.firstName), text(r.surname)].filter(Boolean).join(' ') || text(r.name) || `Patron ${r.id as number}`,
      detail: [text(r.cardNumber), text(r.categoryName), text(r.homeLibraryName)].filter(Boolean).join('  ·  '),
    }));
}

export function toBiblioHits(payload: unknown): BiblioHit[] {
  return rowsOf(payload)
    .filter((r) => typeof r.id === 'number')
    .map((r) => ({
      id: r.id as number,
      title: text(r.title) || `Record ${r.id as number}`,
      detail: [text(r.author), text(r.isbn), text(r.publicationYear)].filter(Boolean).join('  ·  '),
    }));
}

export interface GlobalSearchResult {
  patrons: PatronHit[];
  biblios: BiblioHit[];
  loading: boolean;
}

/** Debounced patron + catalogue lookup for the command palette. Failures degrade silently to "no results". */
export function useGlobalSearch(query: string, enabled: { patrons: boolean; biblios: boolean }): GlobalSearchResult {
  const q = useDebounce(query.trim(), 220);
  const ready = q.length >= 2;
  const patrons = useQuery({
    queryKey: ['palette', 'patrons', q],
    enabled: ready && enabled.patrons,
    retry: false,
    staleTime: 15_000,
    queryFn: ({ signal }) => api.get<unknown>(SEARCH_ENDPOINTS.patrons, { query: { q, pageSize: 5 }, signal }).then(toPatronHits),
  });
  const biblios = useQuery({
    queryKey: ['palette', 'biblios', q],
    enabled: ready && enabled.biblios,
    retry: false,
    staleTime: 15_000,
    queryFn: ({ signal }) => api.get<unknown>(SEARCH_ENDPOINTS.biblios, { query: { q, pageSize: 5 }, signal }).then(toBiblioHits),
  });
  return {
    patrons: ready ? (patrons.data ?? []) : [],
    biblios: ready ? (biblios.data ?? []) : [],
    loading: query.trim() !== q || patrons.isFetching || biblios.isFetching,
  };
}

export interface RecentEntry {
  id: string;
  label: string;
  to: string;
}

/** Last few palette destinations, newest first. */
export function useRecentCommands(): [RecentEntry[], (entry: RecentEntry) => void] {
  const [recent, setRecent] = useStoredState<RecentEntry[]>('lumina.palette.recent.v1', []);
  const push = (entry: RecentEntry): void => setRecent((prev) => [entry, ...prev.filter((e) => e.id !== entry.id)].slice(0, 6));
  return [Array.isArray(recent) ? recent : [], push];
}
